import type { BundleType } from '@mcgames/contracts';
import { Spinner } from '@mcgames/ui';
import { useTranslation } from 'react-i18next';
import { isNotFound, useErrorText, useLatestRelease } from '../api/hooks.ts';
import styles from './ReleaseDownloads.module.css';

const BUNDLE_LABEL_KEYS: Record<BundleType, string> = {
  appimage: 'download.appimage',
  deb: 'download.deb',
};

/** Version, notes and one download link per bundle of the latest client release. */
export function ReleaseDownloads() {
  const { t, i18n } = useTranslation('admin');
  const errorText = useErrorText();
  const latest = useLatestRelease();

  if (latest.isPending) return <Spinner />;
  if (latest.isError) {
    return isNotFound(latest.error) ? (
      <p>{t('download.noRelease')}</p>
    ) : (
      <p className={styles['error']} role="alert">
        {errorText(latest.error)}
      </p>
    );
  }

  const release = latest.data;
  return (
    <div className={styles['release']}>
      <p className={styles['meta']}>
        <strong>{t('download.version', { version: release.version })}</strong>
        <span>
          {t('download.released', {
            date: new Date(release.pubDate).toLocaleDateString(i18n.language),
          })}
        </span>
      </p>
      <ul className={styles['links']} aria-label={t('download.linksLabel')}>
        {release.bundles.map((bundle) => (
          <li key={bundle}>
            <a className={styles['link']} href={`/api/client/download/${bundle}`}>
              {t(BUNDLE_LABEL_KEYS[bundle])}
            </a>
          </li>
        ))}
      </ul>
      {release.notes !== '' && (
        <section aria-labelledby="release-notes-title">
          <h2 id="release-notes-title" className={styles['notesTitle']}>
            {t('download.notes')}
          </h2>
          <p className={styles['notes']}>{release.notes}</p>
        </section>
      )}
    </div>
  );
}
